import Vue from 'vue'

const STORAGE_KEY = 'tags_view_visited'

// 从本地存储中恢复已打开的标签（刷新/重开标签页/重开浏览器都不会丢失）
function readStoredViews() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY) || '[]'
    const views = JSON.parse(raw)
    return Array.isArray(views) ? views : []
  } catch (e) {
    return []
  }
}

// 刷新后实例缓存已失效，按恢复的标签重建需要缓存的组件名
function getCachedNames(views) {
  return views
    .filter(v => v.name && !(v.meta && v.meta.noCache))
    .map(v => v.name)
}

// $route 的 matched 数组中，route record 上挂着真实的 Vue 实例（vue-router 的 instances 属性），
// 存在循环引用，直接 JSON.stringify 会抛错，因此只保留标签渲染/跳转必需的字段
function toStorableView(view) {
  return {
    path: view.path,
    fullPath: view.fullPath || view.path,
    name: view.name,
    title: view.title || (view.meta && view.meta.title) || 'unnamed',
    meta: view.meta ? { ...view.meta } : {}
  }
}

export function persistVisitedViews(visitedViews) {
  try {
    const data = visitedViews.map(toStorableView)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch (e) {
    // 存储不可用时忽略，不影响标签功能
  }
}

const storedViews = readStoredViews()

const tagsView = {
  state: {
    visitedViews: storedViews,
    cachedViews: getCachedNames(storedViews)
  },
  mutations: {
    ADD_VISITED_VIEW: (state, view) => {
      if (state.visitedViews.some(v => v.path === view.path)) return
      state.visitedViews.push(
        Object.assign({}, view, {
          title: (view.meta && view.meta.title) || 'unnamed'
        })
      )
    },
    ADD_CACHED_VIEW: (state, view) => {
      if (!view.name) return
      if (state.cachedViews.includes(view.name)) return
      // meta.noCache 的页面（详情页/表单页等）只显示标签，不缓存实例
      if (view.meta && view.meta.noCache) return
      state.cachedViews.push(view.name)
    },

    DEL_VISITED_VIEW: (state, view) => {
      for (const [i, v] of state.visitedViews.entries()) {
        if (v.path === view.path) {
          state.visitedViews.splice(i, 1)
          break
        }
      }
    },
    DEL_CACHED_VIEW: (state, view) => {
      const index = state.cachedViews.indexOf(view.name)
      if (index > -1) {
        state.cachedViews.splice(index, 1)
      }
    },

    DEL_OTHERS_VISITED_VIEWS: (state, view) => {
      state.visitedViews = state.visitedViews.filter(v => v.meta.affix || v.path === view.path)
    },
    DEL_OTHERS_CACHED_VIEWS: (state, view) => {
      const index = state.cachedViews.indexOf(view.name)
      if (index > -1) {
        state.cachedViews = state.cachedViews.slice(index, index + 1)
      } else {
        state.cachedViews = []
      }
    },

    DEL_ALL_VISITED_VIEWS: state => {
      // 保留固定标签（affix，如首页）
      state.visitedViews = state.visitedViews.filter(tag => tag.meta && tag.meta.affix)
    },
    DEL_ALL_CACHED_VIEWS: state => {
      state.cachedViews = []
    },

    UPDATE_VISITED_VIEW: (state, view) => {
      for (let v of state.visitedViews) {
        if (v.path === view.path) {
          v = Object.assign(v, view)
          break
        }
      }
    }
  },
  actions: {
    addView({ dispatch }, view) {
      dispatch('addVisitedView', view)
      dispatch('addCachedView', view)
    },
    addVisitedView({ commit }, view) {
      commit('ADD_VISITED_VIEW', view)
    },
    addCachedView({ commit }, view) {
      commit('ADD_CACHED_VIEW', view)
    },

    delView({ dispatch, state }, view) {
      return new Promise(resolve => {
        dispatch('delVisitedView', view)
        dispatch('delCachedView', view)
        resolve({
          visitedViews: [...state.visitedViews],
          cachedViews: [...state.cachedViews]
        })
      })
    },
    delVisitedView({ commit, state }, view) {
      return new Promise(resolve => {
        commit('DEL_VISITED_VIEW', view)
        resolve([...state.visitedViews])
      })
    },
    delCachedView({ commit, state }, view) {
      return new Promise(resolve => {
        commit('DEL_CACHED_VIEW', view)
        resolve([...state.cachedViews])
      })
    },

    delOthersViews({ dispatch, state }, view) {
      return new Promise(resolve => {
        dispatch('delOthersVisitedViews', view)
        dispatch('delOthersCachedViews', view)
        resolve({
          visitedViews: [...state.visitedViews],
          cachedViews: [...state.cachedViews]
        })
      })
    },
    delOthersVisitedViews({ commit, state }, view) {
      return new Promise(resolve => {
        commit('DEL_OTHERS_VISITED_VIEWS', view)
        resolve([...state.visitedViews])
      })
    },
    delOthersCachedViews({ commit, state }, view) {
      return new Promise(resolve => {
        commit('DEL_OTHERS_CACHED_VIEWS', view)
        resolve([...state.cachedViews])
      })
    },

    delAllViews({ dispatch, state }) {
      return new Promise(resolve => {
        dispatch('delAllVisitedViews')
        dispatch('delAllCachedViews')
        resolve({
          visitedViews: [...state.visitedViews],
          cachedViews: [...state.cachedViews]
        })
      })
    },
    delAllVisitedViews({ commit, state }) {
      return new Promise(resolve => {
        commit('DEL_ALL_VISITED_VIEWS')
        resolve([...state.visitedViews])
      })
    },
    delAllCachedViews({ commit, state }) {
      return new Promise(resolve => {
        commit('DEL_ALL_CACHED_VIEWS')
        resolve([...state.cachedViews])
      })
    },

    updateVisitedView({ commit }, view) {
      commit('UPDATE_VISITED_VIEW', view)
    }
  }
}

export default tagsView



// WEBPACK FOOTER //
// ./src/store/modules/tagsView.js